// Shared marketing building blocks used by the public landing page and dedicated marketing pages.
import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { notify as toast } from "../ui/useToast";
import api from "../../lib/api";
import Captcha, { resetCaptcha } from "../Captcha";
import { LogoFull, LogoMark } from "../Logo";
import LanguageSwitcher from "../LanguageSwitcher";
import ButtonLink from "./ButtonLink";
import {
  CTA_PRIMARY_LABEL,
  CTA_PRIMARY_LABEL_SHORT,
  CTA_SECONDARY_LABEL,
  DEMO_BOOKING_ROUTE,
  MARKETING_NAV,
  MARKETING_NAV_SECONDARY,
  RESOURCE_ITEMS,
  SOLUTIONS_ITEMS,
} from "./navigation";
import { Button, Input, Label, Select, Textarea } from "../ui/primitives";
import { Modal } from "../ui/Modal";
import { GlobalThemeToggle } from "../../design/theme";
import BrandWordmarkText from "../BrandWordmarkText";
import { cn } from "../../lib/utils";
import {
  ArrowRight,
  Bell,
  Building2,
  CalendarCheck,
  Check,
  ChevronDown,
  Cookie,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  MessageSquareText,
  Phone,
  ShieldCheck,
  Smartphone,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { MotionPauseButton } from "./MarketingMotion";
import { requestCookiePreferences } from "../../lib/cookieConsent";
import { track, trackPhoneCallClick, trackWhatsAppClick, utmLeadFields, whatsappUrlWithContext } from "../../lib/analytics";
import { ALL_PHONES, REGISTERED_ADDRESS, SALES_EMAIL, SALES_PHONE } from "../../lib/contactInfo";
import { copyrightYear } from "../../lib/academicYear";
import { useMarketingContent } from "../../lib/marketingContent";
import { SOCIAL_ICONS, SOCIAL_PLATFORM_LABELS, validSocialLinks } from "./socialIcons";

export const APP_SCREENS = ["Today's timetable", "Attendance", "Fees", "Homework", "Messages", "Results", "Profile", "Inbox"];

// The ONE brand-blue CTA gradient (rest + hover). Per-site sizing/shadow stays local —
// hierarchy (hero louder than nav) is deliberate — but the gradient itself must never fork.
export const CTA_GRADIENT = "bg-[linear-gradient(135deg,#2563EB,#1D4ED8)]";
export const CTA_GRADIENT_HOVER = "hover:bg-[linear-gradient(135deg,#1D4ED8,#1E40AF)]";

// A11Y-2 (2026-08 marketing audit): the ONE AA-safe warm CTA gradient (moved here from
// parts4.js so the shared chrome can use it without a circular chunk pull; parts4 re-exports).
// Every stop must hold >= 4.5:1 against its white 14px/500 text — axe buckets gradients
// "incomplete", so the ratio is proven mathematically (WCAG relative-luminance formula):
//   #C2410C (orangeWarm-700): L = 0.1685 → (1.0 + 0.05) / (0.1685 + 0.05) = 4.80:1  PASS
//   #9A3412 (orangeWarm-800): L = 0.0937 → (1.0 + 0.05) / (0.0937 + 0.05) = 7.31:1  PASS
// The retired stops failed: white on #F97316 = 2.80:1, on #FB923C = 2.26:1, on #EA580C = 3.56:1.
// (#C2410C is the design system's blessed "white-text-on-orange fill" token — tailwind.config.js.)
export const ORANGE_CTA_GRADIENT = "bg-[linear-gradient(135deg,#C2410C,#9A3412)]";
export const ORANGE_CTA_GRADIENT_HOVER = "hover:bg-[linear-gradient(135deg,#9A3412,#7C2D12)]";

// One request channel for every public marketing surface. A real href remains on each CTA for
// no-JavaScript and modified-click/new-tab use; an ordinary in-app activation is intercepted and
// announced here so the page's single MarketingTopBar can open the canonical contextual dialog.
export const BOOK_DEMO_EVENT = "vp360:book-demo";

export function isPlainBookDemoActivation(event) {
  return !event?.defaultPrevented
    && (event?.button === undefined || event.button === 0)
    && !event?.metaKey
    && !event?.ctrlKey
    && !event?.shiftKey
    && !event?.altKey;
}

export function requestBookDemo(detail = {}) {
  if (typeof window === "undefined") return false;
  window.dispatchEvent(new CustomEvent(BOOK_DEMO_EVENT, { detail }));
  return true;
}

export function BookDemoLink({
  contextLabel,
  placement = "marketing_body",
  onBeforeOpen,
  onClick,
  ...props
}) {
  const location = useLocation();
  return (
    <ButtonLink
      {...props}
      to={DEMO_BOOKING_ROUTE}
      onClick={(event) => {
        onClick?.(event);
        if (!isPlainBookDemoActivation(event)) return;
        event.preventDefault();
        onBeforeOpen?.();
        const page = location.pathname;
        track("demo_request", { placement, context: contextLabel || "current_page", page });
        requestBookDemo({ contextLabel, placement, analyticsTracked: true });
      }}
    />
  );
}

// ── Abstract mockups (pure CSS — no image/network load) ──────────────────────────────────────────
export function PhoneMock({ screens, accent = "primary", small }) {
  const warm = accent === "orange";
  return (
    <div className={`relative rounded-[2rem] border-[6px] border-[#0F1D31] bg-surface shadow-pop overflow-hidden shrink-0 ${small ? "w-36 sm:w-40" : "w-40 sm:w-48"}`} aria-hidden="true">
      <div className="h-5 bg-[#0F1D31] flex items-center justify-center"><span className="w-9 h-1 rounded-full bg-white/30" /></div>
      <div className="p-2.5 space-y-2 bg-muted">
        <div className="flex items-center justify-between">
          <div className="h-2 w-12 rounded bg-line" />
          <span className={`relative w-6 h-6 rounded-full grid place-items-center ${warm ? "bg-orange/15" : "bg-accent/15"}`}>
            <Bell className={`w-3 h-3 ${warm ? "text-orange" : "text-accent"}`} />
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-orange ring-2 ring-surface" />
          </span>
        </div>
        {screens.map((s, i) => (
          <div key={s} className="rounded-lg border border-line bg-surface px-2 py-1.5 flex items-center gap-2">
            <span className={`w-5 h-5 rounded shrink-0 ${i % 2 ? (warm ? "bg-orange/15" : "bg-accent/15") : "bg-muted"}`} />
            <span className="text-[9px] text-ink-2 truncate flex-1">{s}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Public demo capture -> PLATFORM_LEADS pipeline (shared by every marketing page). ──────────
const DEMO_CONTEXT = Object.freeze({
  "/modules": "the module catalogue",
  "/pricing": "plans, charges and rollout scope",
  "/mobile-app": "the parent, student and staff mobile experience",
  "/switch": "migration and switching",
  "/aira": "Aira AI",
  "/security": "security and implementation controls",
  "/compliance/dpdp": "DPDP readiness",
  "/contact": "your school's requirements",
});

function demoContextFor(pathname, explicit) {
  if (explicit) return explicit;
  const path = (pathname || "").replace(/\/+$/, "") || "/";
  if (DEMO_CONTEXT[path]) return DEMO_CONTEXT[path];
  if (path.startsWith("/modules/")) {
    return path.split("/").pop().replace(/-/g, " ");
  }
  if (path.startsWith("/solutions/")) {
    const segment = path.split("/").pop().replace(/-/g, " ");
    return `${segment} school operations`;
  }
  return "your school's workflows";
}

export function demoLeadSource(pathname) {
  const path = (pathname || "/").replace(/^\/+|\/+$/g, "");
  if (!path) return "demo:landing";
  const parts = path.split("/").filter(Boolean);
  // Buyer guides are nine distinct school-job journeys. Keep that attribution before the generic
  // module branch, otherwise every /modules/guides/:key request collapses to demo:module:guides.
  if (parts[0] === "modules" && parts[1] === "guides" && parts[2]) {
    return `demo:guide:${parts[2]}`.slice(0, 40);
  }
  if (parts[0] === "modules" && parts[1]) return `demo:module:${parts[1]}`.slice(0, 40);
  if (parts[0] === "solutions" && parts[1]) return `demo:solution:${parts[1]}`.slice(0, 40);
  return `demo:${parts.join(":")}`.slice(0, 40);
}

// CRO-8 — ONE light-qualification vocabulary for every public lead form (the ContactSalesModal
// here, the landing QuickEnquiry in landingparts/parts4.js and the /contact form). Both fields
// are optional and neither gates submit; they exist so sales stops sizing every lead from zero.
//
// `student_count` is a first-class whitelisted field on PublicPlatformEnquiryRequest, so the band
// posts as a number — the value is the band CEILING (an approximate size signal, never a head
// count). The buyer ROLE has no field of its own and the request model is `extra="forbid"`, so it
// rides at the FRONT of the existing `message` field in one canonical shape.
export const LEAD_STUDENT_BANDS = Object.freeze([
  { value: "200", label: "Up to 200" },
  { value: "500", label: "201 – 500" },
  { value: "1000", label: "501 – 1,000" },
  { value: "2500", label: "1,001 – 2,500" },
  { value: "5000", label: "More than 2,500" },
]);

export const LEAD_ROLE_OPTIONS = Object.freeze([
  "Owner / Trustee",
  "Principal",
  "Administrator",
  "IT",
]);

/** Fold the optional buyer role into the enquiry `message` (max 1000 server-side). */
export function withLeadRole(message, role) {
  const body = (message || "").trim();
  if (!role) return message || "";
  return `Role: ${role}.${body ? ` ${body}` : ""}`.slice(0, 1000);
}

function FieldError({ id, children }) {
  if (!children) return null;
  return <p id={id} className="mt-1.5 text-xs font-medium text-danger" role="alert">{children}</p>;
}

export function ContactSalesModal({ open, onClose, contextLabel, placement = "marketing_demo" }) {
  const location = useLocation();
  const context = demoContextFor(location.pathname, contextLabel);
  const [f, setF] = useState({ school_name: "", contact_name: "", email: "", phone: "", city: "", message: "" });
  // CRO-8 parity with the landing QuickEnquiry + /contact: one optional size band so sales
  // stops sizing every lead by phone from zero. The value is the band CEILING — an
  // approximate size signal, never a head count. Skipping it changes nothing.
  const [studentBand, setStudentBand] = useState("");
  const [buyerRole, setBuyerRole] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const nameRef = useRef(null);
  const schoolRef = useRef(null);
  const emailRef = useRef(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const close = () => {
    if (sent) {
      setF({ school_name: "", contact_name: "", email: "", phone: "", city: "", message: "" });
      setStudentBand("");
      setBuyerRole("");
      setSent(false);
    }
    setErrors({});
    setSubmitError("");
    setCaptchaToken("");
    resetCaptcha();
    onClose?.();
  };

  const validate = () => {
    const next = {};
    if (!f.contact_name.trim()) next.contact_name = "Enter your full name.";
    if (!f.school_name.trim()) next.school_name = "Enter your school name.";
    if (!f.email.trim()) next.email = "Enter your work email.";
    else if (!/^\S+@\S+\.\S+$/.test(f.email.trim())) next.email = "Use an email such as name@yourschool.edu.in.";
    setErrors(next);
    const first = [
      ["contact_name", nameRef],
      ["email", emailRef],
      ["school_name", schoolRef],
    ].find(([key]) => next[key]);
    first?.[1]?.current?.focus();
    return Object.keys(next).length === 0;
  };

  const submit = async (event) => {
    event?.preventDefault();
    if (!validate()) return;
    setSubmitError("");
    setBusy(true);
    try {
      await api.post("/public/platform-enquiry", {
        ...utmLeadFields(),
        ...f,
        // PublicPlatformEnquiryRequest deliberately has no arbitrary metadata bag. Encode the
        // bounded page/module origin in its existing 40-character `source` field while UTM stays
        // in the separately governed `campaign` field.
        message: withLeadRole(f.message, buyerRole),
        ...(studentBand ? { student_count: Number(studentBand) } : {}),
        source: demoLeadSource(location.pathname),
        captcha_token: captchaToken || undefined,
      });
      track("demo_form_submitted", { placement, page: location.pathname, context });
      setSent(true);
    } catch (e) {
      // CRO-5: a prod form outage must be analytically visible, not silent (no PII in props).
      track("lead_submit_failed", { placement, http_status: e?.response?.status ?? 0 });
      const detail = e.response?.data?.detail;
      const message = typeof detail === "string" ? detail : "We couldn't send your request. Check your connection and try again.";
      setSubmitError(message);
      toast.error(message);
    } finally {
      resetCaptcha();
      setCaptchaToken("");
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={close}
      title="Book a free demo"
      description={`Request a guided Vidyapeeth360 walkthrough focused on ${context}.`}
      size="xl"
      initialFocusRef={nameRef}
      // CookieConsent yields for every active modal layer, so the dialog owns one visual and
      // interaction plane instead of competing with the first-visit notice.
      layerClassName="landing-page"
      footer={sent
        ? <Button onClick={close}>Done</Button>
        : <>
            <Button variant="ghost" onClick={close}>Cancel</Button>
            <Button
              type="submit"
              form="marketing-demo-form"
              loading={busy}
              data-testid="sales-submit"
              // A11Y-2: the AA-proven warm gradient (a background-image paints over the Button
              // variant's background-color, so no !important override is needed for the fill).
              className={`group !rounded-full !border-transparent ${ORANGE_CTA_GRADIENT} !px-6 !text-white shadow-[0_10px_26px_rgba(154,52,18,0.28)] ${ORANGE_CTA_GRADIENT_HOVER}`}
            >
              {/* CRO-9: one demo-CTA vocabulary — the submit uses the shared constant, not a
                  fifth hand-typed variant ("Request Demo"). */}
              {CTA_SECONDARY_LABEL} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </Button>
          </>}
    >
      {sent ? (
        <div className="grid min-h-[24rem] place-items-center px-2 py-8 text-center" data-testid="sales-sent" role="status">
          <div className="max-w-md">
            <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-positive-soft text-positive">
              <Check className="h-7 w-7" strokeWidth={2.3} aria-hidden="true" />
            </span>
            <h4 className="mt-5 font-heading text-2xl font-bold tracking-tight text-ink">Your demo request is with our team.</h4>
            <p className="mt-3 text-sm leading-6 text-ink-2">
              We’ll read what you asked us to focus on, look at how your school runs, and come back with the right next step.
            </p>
            {/* CRO-4: real next steps instead of a dead-end "Done" — the self-serve trial and the
                verified WhatsApp channel. (No response-time promise here until product signs off
                on publishing the internal SLA.) */}
            <div className="mt-6 rounded-2xl border border-line bg-muted/45 p-4 text-left">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-ink-subtle">While you wait</p>
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <ButtonLink to="/register" onClick={close} data-testid="sales-sent-trial" className="!rounded-full justify-center">
                  {CTA_PRIMARY_LABEL} <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </ButtonLink>
                <a
                  // CRO-11: the chat opens with the evaluation context already typed (visible and
                  // editable before the visitor sends it) — the same helper the footer, the phone
                  // menu, the landing strip and /contact use.
                  href={whatsappUrlWithContext("a demo request")}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackWhatsAppClick("demo_request_sent")}
                  data-testid="sales-sent-whatsapp"
                  className="inline-flex min-h-11 items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-semibold text-ink-2 transition-[background-color,border-color,color] duration-150 hover:border-accent/30 hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55"
                >
                  <MessageCircle className="h-4 w-4 text-positive" aria-hidden="true" /> WhatsApp our team
                </a>
              </div>
              <p className="mt-3 text-xs leading-5 text-ink-subtle">
                The trial is self-serve and doesn’t wait on this request; response channels are listed on the{" "}
                <Link to="/contact" onClick={close} className="font-semibold text-accent-active hover:underline">contact page</Link>.
              </p>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-[1.35rem] border border-primary/10 bg-surface">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_8%_18%,rgb(var(--vp-primary)/0.10),transparent_31%),radial-gradient(circle_at_92%_88%,rgb(var(--vp-orange-text)/0.08),transparent_27%)]" aria-hidden="true" />
          <div className="relative grid lg:grid-cols-[16.5rem_minmax(0,1fr)]">
            <aside className="border-b border-line bg-muted/45 p-4 sm:p-6 lg:border-b-0 lg:border-r lg:p-7" aria-label="What to expect from the demo">
              <span className="inline-flex min-h-8 items-center gap-2 rounded-full border border-orange-border bg-orange-soft px-3 text-xs font-semibold text-orange-text">
                <CalendarCheck className="h-3.5 w-3.5" aria-hidden="true" /> Guided product demo
              </span>
              <h3 className="mt-4 font-heading text-[1.5rem] font-bold leading-[1.08] tracking-[-0.035em] text-ink sm:mt-5 sm:text-[1.7rem]">
                See <BrandWordmarkText /> in action.
              </h3>
              <p className="mt-3 text-sm leading-5 text-ink-2 sm:mt-4 sm:leading-6">
                We’ll focus the walkthrough on <strong className="font-semibold text-ink">{context}</strong>, then map the connected records, roles and rollout needs around it.
              </p>
              <div className="mt-6 hidden border-t border-line pt-5 sm:block">
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-ink-subtle">What to expect</p>
                <ul className="mt-3 space-y-3">
                  {[
                    [UsersRound, "School-specific review", "Your workflows and team roles"],
                    [ShieldCheck, "Purpose-limited enquiry", "Only details needed for follow-up"],
                    [CalendarCheck, "Reviewed next step", "No automatic subscription or charge"],
                  ].map(([Icon, title, copy]) => (
                    <li key={title} className="flex items-start gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl border border-line bg-surface text-accent-active shadow-sm">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span className="min-w-0 pt-0.5">
                        <span className="block text-xs font-semibold text-ink">{title}</span>
                        <span className="mt-0.5 block text-[11px] leading-4 text-ink-subtle">{copy}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </aside>

            <form id="marketing-demo-form" onSubmit={submit} noValidate className="min-w-0 p-4 sm:p-6 lg:p-7">
              <div className="mb-4 flex flex-wrap items-center justify-between gap-2 sm:mb-5">
                <div>
                  <p className="font-heading text-lg font-semibold text-ink">Tell us about your school</p>
                  <p className="mt-1 text-xs text-ink-subtle"><span aria-hidden="true">*</span> Required fields</p>
                </div>
                <Link to="/register" onClick={close} className="hidden min-h-11 items-center rounded-lg px-2 text-xs font-semibold text-accent-active hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55 sm:inline-flex">
                  Prefer self-serve? {CTA_PRIMARY_LABEL}
                </Link>
              </div>

              <div className="grid gap-x-4 gap-y-4 sm:grid-cols-2">
                <div>
                  <Label htmlFor="demo-contact-name">Full name <span className="text-danger" aria-hidden="true">*</span></Label>
                  <Input ref={nameRef} id="demo-contact-name" name="contact_name" autoComplete="name" leadingIcon={UserRound} required
                    value={f.contact_name} onChange={(e) => { set("contact_name", e.target.value); if (errors.contact_name) setErrors((p) => ({ ...p, contact_name: "" })); }}
                    aria-invalid={Boolean(errors.contact_name)} aria-describedby={errors.contact_name ? "demo-contact-name-error" : undefined}
                    placeholder="Enter your name" className="mt-1.5 h-12 rounded-xl bg-surface" />
                  <FieldError id="demo-contact-name-error">{errors.contact_name}</FieldError>
                </div>
                <div>
                  <Label htmlFor="demo-email">Work email <span className="text-danger" aria-hidden="true">*</span></Label>
                  <Input ref={emailRef} id="demo-email" name="email" type="email" inputMode="email" autoComplete="email" spellCheck="false" leadingIcon={Mail} required
                    value={f.email} onChange={(e) => { set("email", e.target.value); if (errors.email) setErrors((p) => ({ ...p, email: "" })); }}
                    aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? "demo-email-error" : undefined}
                    placeholder="name@yourschool.edu.in" className="mt-1.5 h-12 rounded-xl bg-surface" data-testid="sales-email" />
                  <FieldError id="demo-email-error">{errors.email}</FieldError>
                </div>
                <div>
                  <Label htmlFor="demo-phone">Mobile number</Label>
                  <Input id="demo-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" leadingIcon={Phone}
                    value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="Enter your mobile number" className="mt-1.5 h-12 rounded-xl bg-surface" />
                </div>
                <div>
                  <Label htmlFor="demo-school">School name <span className="text-danger" aria-hidden="true">*</span></Label>
                  <Input ref={schoolRef} id="demo-school" name="school_name" autoComplete="organization" leadingIcon={Building2} required
                    value={f.school_name} onChange={(e) => { set("school_name", e.target.value); if (errors.school_name) setErrors((p) => ({ ...p, school_name: "" })); }}
                    aria-invalid={Boolean(errors.school_name)} aria-describedby={errors.school_name ? "demo-school-error" : undefined}
                    placeholder="Enter school name" className="mt-1.5 h-12 rounded-xl bg-surface" data-testid="sales-school" />
                  <FieldError id="demo-school-error">{errors.school_name}</FieldError>
                </div>
                <div>
                  <Label htmlFor="demo-city">City</Label>
                  <Input id="demo-city" name="city" autoComplete="address-level2" leadingIcon={MapPin}
                    value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Enter your city" className="mt-1.5 h-12 rounded-xl bg-surface" />
                </div>
                <div>
                  <Label htmlFor="demo-students">Students <span className="font-normal text-ink-subtle">(approx., optional)</span></Label>
                  <Select id="demo-students" value={studentBand} onChange={(e) => setStudentBand(e.target.value)}
                    className="mt-1.5 h-12 rounded-xl bg-surface" data-testid="sales-students">
                    <option value="">Select a range</option>
                    {LEAD_STUDENT_BANDS.map((band) => (
                      <option key={band.value} value={band.value}>{band.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label htmlFor="demo-role">Your role <span className="font-normal text-ink-subtle">(optional)</span></Label>
                  <Select id="demo-role" value={buyerRole} onChange={(e) => setBuyerRole(e.target.value)}
                    className="mt-1.5 h-12 rounded-xl bg-surface" data-testid="sales-role">
                    <option value="">Select your role</option>
                    {LEAD_ROLE_OPTIONS.map((role) => <option key={role} value={role}>{role}</option>)}
                  </Select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="demo-message">Message <span className="font-normal text-ink-subtle">(optional)</span></Label>
                  <div className="relative mt-1.5">
                    <MessageSquareText className="pointer-events-none absolute left-3 top-3.5 z-[1] h-4 w-4 text-ink-subtle" aria-hidden="true" />
                    <Textarea id="demo-message" name="message" value={f.message} onChange={(e) => set("message", e.target.value)}
                      placeholder="What should we focus on?" className="min-h-12 resize-y rounded-xl bg-surface pl-9" />
                  </div>
                </div>
              </div>

              {submitError && (
                <p className="mt-4 rounded-xl border border-danger/25 bg-danger-soft px-3 py-2.5 text-sm text-danger" role="alert" data-testid="sales-error">
                  {submitError}
                </p>
              )}
              <Captcha onToken={setCaptchaToken} action="demo_enquiry" className="mt-5 flex justify-center" />
            </form>
          </div>
        </div>
      )}
    </Modal>
  );
}

// UX-04/A11Y-9 — the shared "Skip to content" link, rendered by MarketingTopBar so EVERY
// marketing page gets it (it used to exist only on the Landing page). Most sub-pages render a
// <main> without an id, so activation falls back to focusing the page's first <main> landmark.
export function SkipToContent() {
  return (
    <a
      href="#main"
      onClick={(event) => {
        const el = document.getElementById("main") || document.querySelector("main");
        if (!el) return;
        event.preventDefault();
        if (!el.hasAttribute("tabindex")) el.setAttribute("tabindex", "-1");
        el.focus({ preventScroll: true });
        el.scrollIntoView({ block: "start" });
      }}
      className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:left-3 focus:z-[60] focus:bg-primary focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
    >
      Skip to content
    </a>
  );
}

// UX-01/UX-02 — a keyboard-complete dropdown for the sub-page desktop header (Solutions /
// Resources). Mirrors the Landing ExploreMenu's WAI-ARIA menu contract exactly (ArrowUp/Down
// cycle, Home/End jump, ArrowDown on the trigger opens + focuses the first item, Esc closes +
// restores the trigger, Tab closes and moves on, click-outside dismisses) without pulling the
// landing chunk into every marketing page.
export function MarketingNavMenu({ label, items, testId }) {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const itemRefs = useRef([]);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") { setOpen(false); triggerRef.current?.focus(); } };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname, location.search]);
  const focusItem = (idx) => {
    const nodes = itemRefs.current.filter(Boolean);
    if (!nodes.length) return;
    nodes[((idx % nodes.length) + nodes.length) % nodes.length]?.focus();
  };
  const onMenuKey = (e) => {
    const nodes = itemRefs.current.filter(Boolean);
    const cur = nodes.indexOf(document.activeElement);
    if (e.key === "ArrowDown") { e.preventDefault(); focusItem(cur + 1); }
    else if (e.key === "ArrowUp") { e.preventDefault(); focusItem(cur - 1); }
    else if (e.key === "Home") { e.preventDefault(); focusItem(0); }
    else if (e.key === "End") { e.preventDefault(); focusItem(nodes.length - 1); }
    else if (e.key === "Tab") setOpen(false);
  };
  const onTriggerKey = (e) => {
    if (e.key === "ArrowDown" || (!open && (e.key === "Enter" || e.key === " "))) {
      e.preventDefault();
      setOpen(true);
      requestAnimationFrame(() => focusItem(0));
    }
  };
  const pathname = location.pathname.length > 1 ? location.pathname.replace(/\/+$/, "") : location.pathname;
  const sectionActive = items.some((item) => item.to.split("?")[0] === pathname);
  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKey}
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid={testId}
        className={cn(
          "inline-flex min-h-10 items-center gap-1 rounded-full px-3 py-2 text-[13.5px] font-semibold tracking-[-0.01em] transition-[background-color,color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55",
          sectionActive || open ? "bg-accent/10 text-accent-active" : "text-ink-2 hover:bg-muted hover:text-ink",
        )}
      >
        {label} <ChevronDown className={cn("h-4 w-4 transition-transform duration-150", open && "rotate-180")} aria-hidden="true" />
      </button>
      {open && (
        <div
          role="menu"
          aria-label={label}
          data-testid={testId ? `${testId}-menu` : undefined}
          onKeyDown={onMenuKey}
          className="absolute left-1/2 top-full z-50 mt-2.5 w-72 -translate-x-1/2 animate-menu-in rounded-2xl border border-line bg-surface/95 p-2 shadow-pop backdrop-blur-2xl origin-top"
        >
          {items.map(({ to, label: itemLabel, icon: Icon, desc, accent }, i) => {
            const isBlue = accent === "blue";
            const setItemRef = (el) => { itemRefs.current[i] = el; };
            return (
              <Link
                key={to}
                to={to}
                role="menuitem"
                ref={setItemRef}
                tabIndex={-1}
                onClick={() => setOpen(false)}
                data-testid={testId ? `${testId}-${to.slice(1).replace(/[^a-zA-Z0-9]+/g, "-")}` : undefined}
                className="flex items-start gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-accent/10 focus-visible:bg-accent/10 focus-visible:outline-none"
              >
                {Icon && (
                  <span className={cn("mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg", isBlue ? "bg-primary/10" : "bg-orange/10")}>
                    <Icon className={cn("h-4 w-4", isBlue ? "text-primary" : "text-orange")} aria-hidden="true" />
                  </span>
                )}
                <span className="min-w-0">
                  <span className="block font-heading text-sm font-semibold leading-tight text-ink">{itemLabel}</span>
                  {desc && <span className="mt-0.5 block text-[12px] text-ink-subtle">{desc}</span>}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── The ONE marketing site header (WP-07) ─────────────────────────────────────────────────────────
// Landing and every dedicated marketing route render this same shell. The component accepts the
// homepage's in-page links and Explore menu as data/slots, while the logo, responsive breakpoint,
// theme control, conversion hierarchy and mobile sheet remain one implementation. The full wordmark
// is never compressed: phones receive the official mark and the real full asset returns at sm/640px.
export function MarketingTopBar({
  onBookDemo,
  navItems = MARKETING_NAV,
  secondaryItems = MARKETING_NAV_SECONDARY,
  // `undefined` (the default) renders the shared Solutions + Resources dropdowns (UX-01/02);
  // the homepage passes its own ExploreMenu; pass `null` explicitly for a bare link row.
  desktopExtra,
  mobileExtraItems = [],
  onNavItem,
  onMobileExtra,
  // UX-07: the language switcher is part of the shared chrome on every marketing page — the
  // homepage-only opt-in left Hindi/Odia switching unreachable from any sub-page.
  showLanguage = true,
  home = false,
}) {
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuMaxHeight, setMenuMaxHeight] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [demoRequest, setDemoRequest] = useState(null);
  const menuButtonRef = React.useRef(null);

  const openDemo = React.useCallback((detail = {}) => {
    setMenuOpen(false);
    if (!home && !detail?.analyticsTracked) {
      track("demo_request", {
        placement: detail?.placement || "marketing_topbar",
        context: detail?.contextLabel || demoContextFor(location.pathname),
        page: location.pathname,
      });
    }
    if (onBookDemo) {
      onBookDemo(detail);
      return;
    }
    setDemoRequest({
      contextLabel: detail?.contextLabel || "",
      placement: detail?.placement || "marketing_topbar",
    });
  }, [home, location.pathname, onBookDemo]);

  useEffect(() => { setMenuOpen(false); }, [location.pathname, location.search, location.hash]);
  useEffect(() => {
    const onRequest = (event) => openDemo(event?.detail || {});
    window.addEventListener(BOOK_DEMO_EVENT, onRequest);
    return () => window.removeEventListener(BOOK_DEMO_EVENT, onRequest);
  }, [openDemo]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    const onResize = () => {
      if (window.innerWidth >= 1280) setMenuOpen(false);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
    };
  }, []);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setMenuOpen(false);
      window.requestAnimationFrame(() => menuButtonRef.current?.focus());
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menuOpen]);
  useEffect(() => {
    const root = document.documentElement;
    if (!menuOpen) {
      delete root.dataset.marketingNavOpen;
      return undefined;
    }
    root.dataset.marketingNavOpen = "true";
    return () => { delete root.dataset.marketingNavOpen; };
  }, [menuOpen]);
  React.useLayoutEffect(() => {
    if (!menuOpen) {
      setMenuMaxHeight(null);
      return undefined;
    }

    let frame = 0;
    let observedBanner = null;
    let bannerResizeObserver = null;
    const measure = () => {
      frame = 0;
      const sheet = document.getElementById("marketing-subnav");
      if (!sheet) return;
      const banner = document.querySelector('[data-testid="cookie-consent"]');
      if (banner !== observedBanner) {
        bannerResizeObserver?.disconnect();
        observedBanner = banner;
        if (banner && typeof ResizeObserver !== "undefined") {
          bannerResizeObserver = new ResizeObserver(scheduleMeasure);
          bannerResizeObserver.observe(banner);
        }
      }
      const sheetTop = sheet.getBoundingClientRect().top;
      const lowerEdge = banner
        ? banner.getBoundingClientRect().top - 10
        : window.innerHeight - 8;
      setMenuMaxHeight(Math.max(0, Math.floor(lowerEdge - sheetTop)));
    };
    const scheduleMeasure = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(measure);
    };

    // The first measurement runs before paint so the sheet never flashes through the banner.
    // Later DOM/viewport changes are coalesced to animation frames.
    measure();
    const bodyObserver = new MutationObserver(scheduleMeasure);
    bodyObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener("resize", scheduleMeasure);
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      bodyObserver.disconnect();
      bannerResizeObserver?.disconnect();
      window.removeEventListener("resize", scheduleMeasure);
    };
  }, [menuOpen]);

  const pathname = location.pathname.length > 1
    ? location.pathname.replace(/\/+$/, "")
    : location.pathname;
  const active = (to) => {
    const [path, query] = to.split("?");
    if (path === "/modules") return pathname === path || pathname.startsWith("/modules/");
    if (pathname !== path) return false;
    // "/contact?intent=product-tour" and "/contact" are DIFFERENT destinations — only the one
    // whose query matches the current URL is the current page.
    return query ? location.search === `?${query}` : !location.search.includes("intent=product-tour");
  };
  const isActiveItem = (item) => item.to ? active(item.to) : pathname === "/" && location.hash === item.href;
  const handleNavClick = (item, event) => {
    onNavItem?.(item, event);
    setMenuOpen(false);
  };
  const handleExtraClick = (item, event) => {
    onMobileExtra?.(item, event);
    setMenuOpen(false);
  };
  const navItemCls = (isActive) => cn(
    "inline-flex min-h-10 items-center rounded-full px-3 py-2 text-[13.5px] font-semibold tracking-[-0.01em] transition-[background-color,color] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55",
    isActive ? "bg-accent/10 text-accent-active" : "text-ink-2 hover:bg-muted hover:text-ink",
  );
  const drawerItemCls = (isActive) => cn(
    "flex min-h-11 items-center rounded-xl px-3.5 py-2.5 text-sm font-semibold transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55",
    isActive ? "bg-accent/10 text-accent-active" : "text-ink-2 hover:bg-muted hover:text-ink",
  );
  const renderNavItem = (item, mobile = false) => {
    const classes = mobile ? drawerItemCls(isActiveItem(item)) : navItemCls(isActiveItem(item));
    if (item.to) {
      return (
        <Link key={item.to} to={item.to} aria-current={isActiveItem(item) ? "page" : undefined}
          onClick={(event) => handleNavClick(item, event)} className={classes}>
          {item.label}
        </Link>
      );
    }
    return (
      <a key={item.href || item.label} href={item.href} aria-current={isActiveItem(item) ? "location" : undefined}
        onClick={(event) => handleNavClick(item, event)} className={classes}>
        {item.label}
      </a>
    );
  };

  const demoControl = (mobile = false) => {
    const className = cn(
      "min-h-11 whitespace-nowrap !rounded-full !px-4 text-[13.5px] font-semibold",
      mobile && "w-full",
    );
    return (
      <ButtonLink to={DEMO_BOOKING_ROUTE} variant="outline" className={className}
        onClick={(event) => {
          setMenuOpen(false);
          if (!isPlainBookDemoActivation(event)) return;
          event.preventDefault();
          openDemo({ placement: home ? "nav" : "marketing_topbar" });
        }} data-testid={mobile ? (home ? "nav-book-demo-mobile" : "sub-book-demo-mobile") : (home ? "nav-book-demo" : "sub-book-demo")}>
        {CTA_SECONDARY_LABEL}
      </ButtonLink>
    );
  };

  const primaryTestId = home ? "nav-register" : "sub-start-trial";

  return (
    <header className="print:hidden sticky top-0 z-50 px-2 py-2 sm:px-3" data-testid="marketing-topbar" data-scrolled={scrolled ? "true" : "false"}>
      {/* UX-04: every sub-page gets the skip link from the shared chrome. The homepage renders
          its own BEFORE the utility contact strip (which precedes this header in its DOM), so
          first-Tab still reveals a skip link there without a duplicate here. */}
      {!home && <SkipToContent />}
      <nav aria-label="Primary" className={cn(
        "relative mx-auto max-w-[90rem] rounded-[1.25rem] border border-line bg-surface/[0.96] shadow-[0_1px_2px_rgba(15,23,42,0.06),0_12px_32px_-24px_rgba(15,23,42,0.42)] backdrop-blur-md transition-[background-color,border-color,box-shadow] duration-200",
        scrolled && "shadow-[0_1px_2px_rgba(15,23,42,0.08),0_18px_45px_-22px_rgba(15,23,42,0.4)]",
      )}>
        <span className="pointer-events-none absolute inset-x-8 top-0 h-px bg-[linear-gradient(90deg,transparent,rgb(var(--vp-accent)/0.62),transparent)]" aria-hidden="true" />
        <div className="flex h-[58px] min-w-0 items-center justify-between gap-2 px-2.5 sm:h-[62px] sm:px-4 xl:px-5">
          <Link to="/" aria-label="Vidyapeeth360 home" className="shrink-0 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55" data-testid="marketing-home-link">
            <span className="hidden sm:inline-flex"><LogoFull height={32} /></span>
            <span className="inline-flex sm:hidden"><LogoMark size={32} /></span>
          </Link>
          {/* A11Y-9: aria-label on a role-less div is ignored by AT — role="group" makes it a
              real labelled grouping in the accessibility tree. */}
          <div role="group" className="hidden min-w-0 items-center gap-0.5 xl:flex" aria-label="Site sections">
            {navItems.map((item) => renderNavItem(item))}
            {desktopExtra === undefined ? (
              <>
                <MarketingNavMenu label="Solutions" items={SOLUTIONS_ITEMS} testId="subnav-solutions" />
                <MarketingNavMenu label="Resources" items={RESOURCE_ITEMS} testId="subnav-resources" />
              </>
            ) : desktopExtra}
          </div>
          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            {/* UX-07: visible from xl (the whole desktop-nav range) — the old 2xl gate left
                1280–1535px with no language affordance (no hamburger there either). */}
            {showLanguage && <span className="hidden xl:inline-flex"><LanguageSwitcher /></span>}
            {/* 2026-08-21 product decision: icon-only at every width — the "Light mode"/"Dark mode"
                word is gone; the sun/moon button (with its aria-label) is the whole control. */}
            <GlobalThemeToggle className="hidden xl:inline-grid" />
            <ButtonLink to="/login" variant="ghost" className="hidden !min-h-10 !rounded-full !px-2.5 text-[13.5px] xl:inline-flex">Sign in</ButtonLink>
            <span className="hidden xl:inline-flex">{demoControl(false)}</span>
            {/* CRO-2: every Start-free-trial CLICK fires trial_cta_click with its placement;
                `trial_start` stays reserved for a COMPLETED registration (Register.js). */}
            <ButtonLink to="/register" onClick={() => track("trial_cta_click", { placement: "topbar" })}
              className="min-h-10 whitespace-nowrap !rounded-full !px-3 text-[13px] sm:!px-4 sm:text-[13.5px]" data-testid={primaryTestId}>
              {/* CRO-9: the phone-width short form is the sanctioned constant, never a bare "Start". */}
              <span className="sm:hidden">{CTA_PRIMARY_LABEL_SHORT}</span>
              <span className="hidden sm:inline">{CTA_PRIMARY_LABEL}</span>
            </ButtonLink>
            <button ref={menuButtonRef} type="button" onClick={() => setMenuOpen((open) => !open)} aria-expanded={menuOpen} aria-controls="marketing-subnav" aria-label={menuOpen ? "Close navigation" : "Open navigation"}
              className="grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-ink transition-[background-color,border-color,transform] duration-150 hover:border-accent/30 hover:bg-muted active:scale-[0.97] focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55 xl:hidden"
              data-testid={home ? "nav-menu-toggle" : "sub-menu-toggle"}>
              {menuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div id="marketing-subnav" className="absolute left-0 right-0 top-[calc(100%+0.5rem)] mx-auto max-h-[calc(100dvh-5.5rem)] w-[calc(100vw-1rem)] max-w-[38rem] overflow-y-auto overscroll-contain rounded-[1.25rem] border border-line bg-surface shadow-[0_22px_70px_-24px_rgba(15,23,42,0.45)] xl:hidden"
            style={menuMaxHeight === null ? undefined : { maxHeight: `${menuMaxHeight}px` }}
            data-testid={home ? "nav-mobile-menu" : "sub-mobile-menu"}>
            <div className="grid gap-1 p-3 sm:grid-cols-2">
              {navItems.map((item) => renderNavItem(item, true))}
              {secondaryItems.map((item) => renderNavItem(item, true))}
              {/* UX-01/SEO-2: the segment family rides every mobile panel (home + sub-pages)
                  under one labelled group, mirroring the desktop Solutions dropdown. */}
              <p className="px-3.5 pt-2 text-[11px] font-bold uppercase tracking-[0.16em] text-ink-subtle sm:col-span-2">Who it&rsquo;s for</p>
              {SOLUTIONS_ITEMS.map((item) => renderNavItem({ to: item.to, label: item.label }, true))}
              {mobileExtraItems.map((item) => {
                const Icon = item.icon;
                if (item.to) {
                  return (
                    <Link key={item.to} to={item.to} onClick={(event) => handleExtraClick(item, event)} className={drawerItemCls(active(item.to))}
                      data-testid={item.to === "/help" ? "nav-help" : undefined}>
                      {Icon && <Icon className={cn("mr-2.5 h-4 w-4 shrink-0", item.accent === "blue" ? "text-accent" : "text-orange-text")} aria-hidden="true" />}
                      {item.label}
                    </Link>
                  );
                }
                return (
                  <button key={item.label} type="button" onClick={(event) => handleExtraClick(item, event)} className={cn(drawerItemCls(false), "w-full text-left")}>
                    {Icon && <Icon className={cn("mr-2.5 h-4 w-4 shrink-0", item.accent === "blue" ? "text-accent" : "text-orange-text")} aria-hidden="true" />}
                    {item.label}
                  </button>
                );
              })}
            </div>
            <div className="grid gap-2 border-t border-line bg-muted/35 p-3 sm:grid-cols-2">
              <div className="flex min-h-11 items-center gap-2">
                {showLanguage && <LanguageSwitcher />}
                <GlobalThemeToggle />
              </div>
              <ButtonLink to="/login" variant="ghost" className="w-full !rounded-full" onClick={() => setMenuOpen(false)}>Sign in</ButtonLink>
              <ButtonLink to="/register" className="w-full !rounded-full" onClick={() => { track("trial_cta_click", { placement: "mobile_nav" }); setMenuOpen(false); }}>{CTA_PRIMARY_LABEL}</ButtonLink>
              {demoControl(true)}
              {/* CRO-11: the WhatsApp-first ICP gets a direct chat row in the phone panel, with
                  the evaluation context prefilled (visible + editable before sending). */}
              <a
                href={whatsappUrlWithContext("the site menu")}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => { trackWhatsAppClick("mobile_nav"); setMenuOpen(false); }}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-semibold text-ink-2 transition-[background-color,border-color,color] duration-150 hover:border-accent/30 hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55 sm:col-span-2"
                data-testid={home ? "nav-mobile-whatsapp" : "sub-mobile-whatsapp"}
              >
                <MessageCircle className="h-4 w-4 text-positive" aria-hidden="true" /> Chat on WhatsApp
              </a>
              {/* The sales number itself: the desktop utility strip is hidden below md, so phones
                  had NO visible number — a call-first evaluator lost the channel entirely. */}
              <a
                href={`tel:${SALES_PHONE.tel}`}
                onClick={() => { trackPhoneCallClick("mobile_nav", "sales"); setMenuOpen(false); }}
                className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full border border-line bg-surface px-4 text-sm font-semibold text-ink-2 transition-[background-color,border-color,color] duration-150 hover:border-accent/30 hover:bg-muted hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-focusring/55 sm:col-span-2"
                data-testid={home ? "nav-mobile-call" : "sub-mobile-call"}
              >
                <Phone className="h-4 w-4 text-accent" aria-hidden="true" /> Call {SALES_PHONE.display}
              </a>
            </div>
          </div>
        )}
      </nav>
      <ContactSalesModal
        open={Boolean(demoRequest)}
        onClose={() => setDemoRequest(null)}
        contextLabel={demoRequest?.contextLabel}
        placement={demoRequest?.placement || "marketing_topbar"}
      />
    </header>
  );
}

// One footer link column. Route links (`/…`) use <Link>; in-page/home anchors (`/#…`) use <a>.
function FooterCol({ title, links }) {
  return (
    <div>
      <h3 className="mb-3 font-heading text-[12px] font-semibold uppercase tracking-[0.17em] text-sky-200/55">{title}</h3>
      <ul className="space-y-0.5 text-sm">
        {links.map(([label, to]) => (
          <li key={label}>
            {to.startsWith("/") && !to.startsWith("/#")
              ? <Link to={to} className="inline-flex min-h-10 items-center rounded text-white/[0.66] transition-colors duration-150 hover:text-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">{label}</Link>
              : <a href={to} className="inline-flex min-h-10 items-center rounded text-white/[0.66] transition-colors duration-150 hover:text-sky-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">{label}</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

// Footer legal links come from the DB (published `legal_documents`) so the platform admin can add, rename,
// or unpublish a policy without a redeploy. Known slugs use their canonical path; anything else the admin
// publishes resolves via /legal/:slug. Falls back to the canonical set if the fetch fails.
const KNOWN_LEGAL_PATHS = {
  "privacy-policy": "/privacy-policy", "terms-and-conditions": "/terms-and-conditions",
  "refund-policy": "/refund-policy", "cookie-policy": "/cookie-policy", "about": "/about",
};
const LEGAL_FALLBACK = [
  ["Terms of Service", "/terms-and-conditions"], ["Privacy Policy", "/privacy-policy"],
  ["Refund Policy", "/refund-policy"], ["Cookie Policy", "/cookie-policy"],
];
function useLegalLinks() {
  const [links, setLinks] = useState(LEGAL_FALLBACK);
  useEffect(() => {
    let alive = true;
    api.get("/public/legal")
      .then(({ data }) => {
        const docs = (data?.documents || []).filter((d) => d.slug && d.slug !== "about");
        if (alive && docs.length) setLinks(docs.map((d) => [d.title, KNOWN_LEGAL_PATHS[d.slug] || `/legal/${d.slug}`]));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return links;
}

// THE canonical marketing footer — the single, consistent footer for every Vidyapeeth360 marketing page
// (Landing, /solutions/:segment, /help, /mobile-app). Uses the brand mark (vidyapeeth-icon.png)
// on the deep-navy panel; anchors are home-relative (`/#…`) so they work from any page. Tenant PUBLIC
// sites (PublicSite) keep their OWN tenant-branded footer — this is Vidyapeeth360's, not a school's.
// Real social profiles ONLY (TRUST-15) — the links are platform-admin-published via
// /platform?view=landing-cms ("social_links" section, core/marketing.py), never hardcoded
// placeholders; the CMS default is an EMPTY list, so the icon row stays out of the DOM until
// real profile URLs are published. validSocialLinks re-filters client-side (known platform +
// https) as defence in depth over the server allowlist.

export function SiteFooter() {
  const legalLinks = useLegalLinks();
  // Footer copy is CMS-owned (cms.footer — /platform?view=landing-cms → "Sitewide footer");
  // the literals below byte-mirror core/marketing.py DEFAULTS["footer"] as the no-record fallback.
  const cms = useMarketingContent();
  const footerCms = cms?.footer || {};
  const socialLinks = validSocialLinks(cms?.social_links?.links);
  return (
    <footer className="relative print:hidden overflow-hidden border-t border-white/10 bg-[#041127] text-white/70" data-testid="site-footer">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_84%_0%,rgba(37,99,235,0.2),transparent_34%)]" aria-hidden="true" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.16] [background-image:linear-gradient(rgba(147,197,253,0.12)_1px,transparent_1px),linear-gradient(90deg,rgba(147,197,253,0.12)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:linear-gradient(to_bottom,black,transparent_48%)]" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-4 pb-10 pt-28 sm:px-5 sm:pb-14 sm:pt-32">
        <div className="grid gap-7 border-b border-white/10 pb-10 lg:grid-cols-[minmax(0,1.35fr)_minmax(20rem,0.65fr)] lg:items-end lg:pb-12">
          <div>
            <p className="font-heading text-[12px] font-semibold uppercase tracking-[0.2em] text-sky-300">{footerCms.eyebrow || "The connected school ERP"}</p>
            <h2 className="mt-3 max-w-3xl text-balance font-heading text-[clamp(2rem,4.6vw,4.6rem)] font-semibold leading-[0.98] tracking-[-0.045em] text-white">
              {footerCms.heading || "Run the school day from one connected system."}
            </h2>
          </div>
          <div className="lg:pb-1">
            <p className="max-w-xl text-sm leading-relaxed text-white/[0.62] sm:text-[15px]">{footerCms.subhead || "Bring admissions, academics, fees, staff and payroll, transport, daily tasks and family communication into one connected school day."}</p>
            <div className="mt-5 flex flex-col gap-2.5 sm:flex-row">
              <BookDemoLink
                bare
                placement="marketing_footer"
                data-testid="footer-primary-cta"
                className="inline-flex min-h-12 items-center justify-center rounded-full bg-[#2563EB] px-5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-[#1D4ED8] focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#041127]"
              >
                {footerCms.primary_cta || "Book a live demo"}
              </BookDemoLink>
              <Link to="/modules" className="inline-flex min-h-12 items-center justify-center rounded-full border border-white/[0.18] bg-white/[0.055] px-5 text-sm font-semibold text-white/[0.88] transition-[background-color,border-color,color] duration-150 hover:border-white/[0.32] hover:bg-white/[0.09] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#041127]">{footerCms.secondary_cta || "Explore 50+ modules"}</Link>
            </div>
          </div>
        </div>

        <div className="grid gap-x-10 gap-y-11 py-10 sm:grid-cols-2 lg:grid-cols-12 lg:py-12">
          <div className="sm:col-span-2 lg:col-span-5">
            <Link to="/" aria-label="Vidyapeeth360 home" className="inline-block rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-sky"><LogoFull height={38} /></Link>
            {/* UX-06: plain language (grade ≈ 6, was 17+) — same claims: people in control,
                role-scoped access, audit evidence. */}
            <p className="mt-4 max-w-md text-sm leading-relaxed text-white/[0.58]">One system for the whole day of an Indian K–12 school. People stay in charge, each role sees only its own work, and every key action leaves a record.</p>

            {/* WP-19: the footer carries all three verified call lines. WhatsApp stays attached to
                the primary sales number, with one sales inbox and purpose-labelled help on /contact. */}
            <div className="mt-6 max-w-lg border-t border-white/10 pt-5">
              <p className="font-heading text-[12px] font-semibold uppercase tracking-[0.17em] text-sky-200/55">Talk to our team</p>
              <div className="mt-1 flex flex-wrap items-center gap-x-5">
                {ALL_PHONES.map((phone, index) => (
                  <a key={phone.tel} href={`tel:${phone.tel}`} aria-label={`Call ${index === 0 ? "sales" : index === 1 ? "onboarding" : "support"} at ${phone.display}`}
                    /* CRO-10: offline-channel intent — the labelled line purpose rides the event,
                       never the phone number (no PII in props). */
                    onClick={() => trackPhoneCallClick("footer", index === 0 ? "sales" : index === 1 ? "onboarding" : "support")}
                    className="inline-flex min-h-12 items-center gap-2.5 rounded text-white/[0.72] transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">
                    <Phone className="h-4 w-4 shrink-0 text-sky-300" aria-hidden="true" />
                    <span className="flex flex-col leading-tight">
                      <span className="text-[12px] font-semibold text-sky-200/70">{index === 0 ? "Sales" : index === 1 ? "Onboarding" : "Support"}</span>
                      <span className="mt-1 text-sm">{phone.display}</span>
                    </span>
                  </a>
                ))}
                {/* CRO-11: context-prefilled click-to-chat (visible + editable before sending). */}
                <a href={whatsappUrlWithContext("the site footer")} target="_blank" rel="noopener noreferrer" onClick={() => trackWhatsAppClick("footer")} className="inline-flex min-h-11 items-center gap-2.5 rounded text-sm text-white/[0.72] transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky"><MessageCircle className="h-4 w-4 shrink-0 text-sky-300" aria-hidden="true" /> WhatsApp</a>
                <a href={`mailto:${SALES_EMAIL}`} className="inline-flex min-h-11 items-center gap-2.5 rounded text-sm text-white/[0.72] transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky"><Mail className="h-4 w-4 shrink-0 text-sky-300" aria-hidden="true" /> {SALES_EMAIL}</a>
              </div>
              <p className="mt-2 flex max-w-md items-start gap-2.5 text-[13px] leading-relaxed text-white/[0.52]"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-sky-300" aria-hidden="true" /> {REGISTERED_ADDRESS}</p>
              <p className="mt-1 text-[13px] text-white/[0.52]">See who to contact, and which inbox to use, on the <Link to="/contact" className="underline decoration-white/30 underline-offset-2 hover:text-white">contact page</Link>.</p>
            </div>

            {socialLinks.length > 0 && (
              <div className="mt-5 flex items-center gap-2" data-testid="footer-social">
                {socialLinks.map(({ platform, url, label }) => {
                  const Icon = SOCIAL_ICONS[platform]; // aria-hidden is baked into the brand SVG
                  return (
                    <a key={`${platform}:${url}`} href={url} target="_blank" rel="noopener noreferrer"
                      aria-label={label || `Vidyapeeth360 on ${SOCIAL_PLATFORM_LABELS[platform]}`}
                      className="grid h-10 w-10 place-items-center rounded-lg border border-white/15 bg-white/5 text-white/60 transition-[border-color,color] duration-150 hover:border-white/30 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky"><Icon className="h-4 w-4" /></a>
                  );
                })}
              </div>
            )}
          </div>

          {/* UX-05/SEO-2: the DESIGN.md-documented 4 link columns — the Solutions family gets its
              own column (it was one "K–12 schools" link filed under Trust) so every segment page
              has a persistent inbound link from every marketing page. */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-8 sm:col-span-2 lg:col-span-7 lg:grid-cols-4 lg:gap-x-8">
            <FooterCol title="Product" links={[["Module catalogue", "/modules"], ["Aira AI", "/aira"], ["Task management", "/modules/task-management"], ["Transport management", "/modules/transport-management"], ["Plans & pricing", "/pricing"], ["Product tour", "/contact?intent=product-tour"], ["Mobile early access", "/mobile-app"]]} />
            <FooterCol title="School workflows" links={[["Admissions", "/modules/student-admission"], ["Fee collection", "/modules/fee-collection"], ["Attendance", "/modules/student-attendance"], ["Report cards", "/modules/result-publishing"], ["Parent communication", "/modules/announcements"]]} />
            <FooterCol title="Solutions" links={[["K–12 schools", "/solutions/school"], ["Preschools", "/solutions/preschool"], ["Colleges & universities", "/solutions/college"], ["Coaching institutes", "/solutions/coaching"], ["Switching & migration", "/switch"]]} />
            <FooterCol title="Trust & company" links={[["Security overview", "/security"], ["DPDP guide", "/compliance/dpdp"], ["Help Center", "/help"], ["About the company", "/about"], ["Contact sales", "/contact"]]} />
          </div>
        </div>

        <div className="flex flex-col gap-5 border-t border-white/10 pt-6">
          <div className="flex flex-wrap items-center gap-2.5">
            <MotionPauseButton />
            <button type="button" onClick={requestCookiePreferences} data-testid="footer-cookie-settings"
              className="inline-flex min-h-11 items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-3.5 py-2 text-sm font-medium text-white/70 transition-[background-color,border-color,color] duration-150 hover:border-sky-300/35 hover:bg-sky-300/[0.08] hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">
              <Cookie className="h-4 w-4" aria-hidden="true" /> Cookie settings
            </button>
          </div>
          {/* WP-17: pr/pb clearance so the fixed Aira launcher (56px + 24px offset, bottom-right)
              never sits ON the legal links — the last interactive row of every marketing page. */}
          <div className="flex flex-col justify-between gap-4 pb-20 pr-2 sm:pb-2 sm:pr-24 text-[13px] text-white/50 lg:flex-row lg:items-center">
            <p>
              © {copyrightYear()} <BrandWordmarkText light className="text-[13px]" /> · Powered by{" "}
              <a href="https://www.helprevx.com" target="_blank" rel="noopener noreferrer" className="rounded text-orangeWarm-300 underline underline-offset-4 transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">HelpRevX Software Private Limited</a>
            {/* Icons8 attribution removed 2026-08-03 — the module-explorer Icons8 PNG set was replaced
                by Lucide glyphs (ISC, no attribution required), and the retired icons8-* files were
                DELETED from public/icons/ 2026-08-18 (VIS-5/DELTA-3). public/icons/ now holds only the
                project-owned module3d/finance3d 3D art, which lib/moduleArt.js still resolves in-app. */}
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {legalLinks.map(([label, to]) => (
                <Link key={to} to={to} className="inline-flex min-h-9 items-center rounded transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">{label}</Link>
              ))}
              <Link to="/account-deletion" className="inline-flex min-h-9 items-center rounded transition-colors duration-150 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-sky">Account deletion</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}

// Back-compat alias — every marketing page should render the same <SiteFooter/>.
export const MarketingFooter = SiteFooter;

// Re-export an icon used by callers that import from this module.
export { Smartphone };
